import { useMemo, type ReactNode } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { COPY } from "../../copy";
import type { P8CumulativeFirstYearPoint } from "../../p8OverviewData";

import {
  ChartHeadline,
  ChartLegend,
  ChartTooltipRows,
  CHART_LEGEND_GAP_CLASS,
  CHART_MARGIN_WITH_AXIS_LABELS,
  CHART_Y_AXIS_WIDTH,
  chartXAxisLabel,
  findNiceStepDollars,
  formatChartAxisCurrency,
  type ChartLegendItem,
  type ChartTooltipRow,
} from "./chartShared";

const IMMEDIATE_COLOR = "#2d2a70";
const CONTINUED_ACTUAL_COLOR = "#C4D1FF";
const CONTINUED_FORECAST_FILL = "#ecebf7";
const CONTINUED_FORECAST_STROKE = "#47477C";
const SPEND_COLOR = "#F26532";
const CONTINUED_FORECAST_LABEL =
  COPY.overview.charts.cumulative.continuedForecastLabel;
const FIRST_YEAR_LABEL = COPY.overview.charts.cumulative.firstYearLabel;

/** Immediate below, dashed continued forecast above: the stack in miniature. */
function StackedBarSwatch() {
  return (
    <span
      className="relative h-2.5 w-2.5 shrink-0 overflow-hidden rounded-[2px]"
      aria-hidden
    >
      <span
        className="absolute inset-x-0 top-0 h-[5px] border border-dashed"
        style={{
          backgroundColor: CONTINUED_FORECAST_FILL,
          borderColor: CONTINUED_FORECAST_STROKE,
        }}
      />
      <span
        className="absolute inset-x-0 bottom-0 h-[5px]"
        style={{ backgroundColor: IMMEDIATE_COLOR }}
      />
    </span>
  );
}

const Y_AXIS_TICK_COUNT = 6;

type CumulativeFirstYearSalesAndSpendChartProps = {
  data: P8CumulativeFirstYearPoint[];
  /** Rendered in the header. No month-paging arrows: the x-axis is cohort maturity. */
  headerAction?: ReactNode;
};

const LEGEND_ITEMS: ChartLegendItem[] = [
  {
    key: "immediate",
    label: "Immediate ad sales",
    color: IMMEDIATE_COLOR,
    tooltip: COPY.overview.charts.cumulative.immediate,
  },
  // Forecast before actual, matching the bar charts this toggles between.
  {
    key: "forecast",
    label: CONTINUED_FORECAST_LABEL,
    color: CONTINUED_FORECAST_FILL,
    dashed: true,
    ringColor: CONTINUED_FORECAST_STROKE,
    tooltip: COPY.overview.charts.cumulative.forecast,
  },
  {
    key: "actual",
    label: "Continued ad sales (actual)",
    color: CONTINUED_ACTUAL_COLOR,
    tooltip: COPY.overview.charts.cumulative.actual,
  },
  {
    key: "adSpend",
    label: "Ad spend",
    color: SPEND_COLOR,
    tooltip: COPY.overview.charts.cumulative.spend,
  },
];

function computeYAxisLayout(
  data: Array<{ continuedForecastTop: number; adSpend: number }>
) {
  const dataMax = Math.max(
    0,
    ...data.map((point) => Math.max(point.continuedForecastTop, point.adSpend))
  );
  const target = Math.max(dataMax, 5000);
  const step = findNiceStepDollars(target / (Y_AXIS_TICK_COUNT - 1));
  const top = step * (Y_AXIS_TICK_COUNT - 1);
  const ticks = Array.from(
    { length: Y_AXIS_TICK_COUNT },
    (_, index) => index * step
  );
  return { domain: [0, top] as [number, number], ticks };
}

type TooltipEntry = {
  dataKey?: string | number;
  value?: number;
  payload?: P8CumulativeFirstYearPoint;
};

type ChartTooltipProps = {
  active?: boolean;
  label?: string;
  payload?: TooltipEntry[];
};

function ChartTooltip({ active, label, payload }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }

  // Same containment and same row order as the monthly chart this toggles from,
  // so neither the hierarchy nor the position of the total reshuffles when the
  // toggle flips. The actual stays indented under the forecast because it is a
  // part of it rather than a sibling slice, which is also why the total sums
  // only immediate and forecast — adding the actual would double-count it.
  const rows: ChartTooltipRow[] = [
    {
      key: "immediate",
      label: "Immediate ad sales",
      value: row.immediateSales,
      depth: 0,
      swatch: { backgroundColor: IMMEDIATE_COLOR },
    },
    {
      key: "forecast",
      label: CONTINUED_FORECAST_LABEL,
      value: row.continuedForecast,
      depth: 0,
      swatch: {
        backgroundColor: CONTINUED_FORECAST_FILL,
        border: `1px dashed ${CONTINUED_FORECAST_STROKE}`,
      },
    },
    {
      key: "actual",
      label: "Continued ad sales (actual)",
      value: row.continuedActual,
      depth: 1,
      swatch: { backgroundColor: CONTINUED_ACTUAL_COLOR },
    },
    {
      key: "firstYearTotal",
      label: FIRST_YEAR_LABEL,
      value: row.immediateSales + row.continuedForecast,
      depth: 0,
      swatchNode: <StackedBarSwatch />,
    },
    {
      key: "adSpend",
      label: "Ad spend",
      value: row.adSpend,
      depth: 0,
      swatch: { backgroundColor: SPEND_COLOR },
      dividerAbove: true,
    },
  ];

  return (
    <div className="bg-fs-surface-primary rounded-md border border-[#dfe0e1] px-3 py-2 text-[14px] leading-[20px] text-[#333] shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
      <div className="mb-1 font-medium">Month {label}</div>
      <ChartTooltipRows rows={rows} />
    </div>
  );
}

export function CumulativeFirstYearSalesAndSpendChart({
  data,
  headerAction,
}: CumulativeFirstYearSalesAndSpendChartProps) {
  const chartData = useMemo(
    () =>
      data.map((point) => ({
        ...point,
        monthLabel: String(point.monthsSinceAcquisition),
        continuedActualTop: point.immediateSales + point.continuedActual,
        continuedForecastTop: point.immediateSales + point.continuedForecast,
        continuedForecastRemainder: Math.max(
          0,
          point.continuedForecast - point.continuedActual
        ),
      })),
    [data]
  );
  const { domain, ticks } = useMemo(
    () => computeYAxisLayout(chartData),
    [chartData]
  );

  return (
    <div className="bg-fs-surface-primary flex h-full min-h-0 w-full flex-col rounded-lg border border-[#dfe0e1] p-6">
      <ChartHeadline
        title={COPY.overview.charts.cumulative.title}
        description={COPY.overview.charts.cumulative.description}
        actions={<div className="flex items-center gap-3">{headerAction}</div>}
      />

      <div
        className={`mt-4 flex min-h-0 flex-1 flex-col ${CHART_LEGEND_GAP_CLASS}`}
      >
        <div className="min-h-0 flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={CHART_MARGIN_WITH_AXIS_LABELS}
            >
              <CartesianGrid
                stroke="#DFE0E1"
                strokeDasharray="3 0"
                vertical={false}
              />
              <XAxis
                dataKey="monthLabel"
                tickLine={{ stroke: "#DFE0E1" }}
                axisLine={{ stroke: "#DFE0E1" }}
                interval={0}
                height={32}
                tick={{ fontSize: 11, fill: "#757575" }}
                label={chartXAxisLabel("Months since conversion")}
              />
              <YAxis
                domain={domain}
                ticks={ticks}
                interval={0}
                tick={{ fontSize: 11, fill: "#757575" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value: number) =>
                  formatChartAxisCurrency(value)
                }
                width={CHART_Y_AXIS_WIDTH}
              />
              <Tooltip
                cursor={{ stroke: "#dfe0e1", strokeWidth: 1 }}
                content={(props) => (
                  <ChartTooltip {...(props as unknown as ChartTooltipProps)} />
                )}
              />
              <Area
                type="linear"
                dataKey="immediateSales"
                stackId="cumulative"
                stroke={IMMEDIATE_COLOR}
                fill={IMMEDIATE_COLOR}
                fillOpacity={1}
                strokeWidth={0}
                dot={false}
                isAnimationActive={false}
              />
              <Area
                type="linear"
                dataKey="continuedActual"
                stackId="cumulative"
                stroke={CONTINUED_ACTUAL_COLOR}
                fill={CONTINUED_ACTUAL_COLOR}
                fillOpacity={1}
                strokeWidth={0}
                dot={false}
                isAnimationActive={false}
              />
              <Area
                type="linear"
                dataKey="continuedForecastRemainder"
                stackId="cumulative"
                stroke={CONTINUED_FORECAST_STROKE}
                fill={CONTINUED_FORECAST_FILL}
                fillOpacity={1}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="linear"
                dataKey="immediateSales"
                stroke={CONTINUED_FORECAST_STROKE}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
              <Line
                type="linear"
                dataKey="adSpend"
                stroke={SPEND_COLOR}
                strokeWidth={1.5}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <ChartLegend items={LEGEND_ITEMS} />
      </div>
    </div>
  );
}
